/**
 * Sentry Error Monitoring Configuration
 *
 * This module configures Sentry for error tracking, performance monitoring, and session replay
 * in the Axle partner portal. Sentry is automatically disabled in local development to prevent
 * test data pollution in production monitoring dashboards.
 *
 * Key Features:
 * - Error tracking with full stack traces
 * - Performance monitoring via browser tracing
 * - Session replay for debugging user interactions
 * - Automatic local development detection
 * - Override capability for local testing
 *
 * Environment Variables:
 * - REACT_APP_SENTRY_IN_LOCAL: Set to 'true' to enable Sentry in local development
 *
 * @see https://docs.sentry.io/platforms/javascript/guides/react/
 */
import * as Sentry from '@sentry/react';

import './polyfills';
import { axleEnvironment, isLocal } from './utils/axleEnvironment';
import { envVars } from './utils/envVars';

/**
 * Sentry Data Source Name (DSN) - identifies the Axle project in Sentry
 * This is safe to expose in client-side code as it's designed to be public
 */
const sentryDsn = 'https://e4f5353083bb4bb7bfe1049398279382@o495775.ingest.sentry.io/5937793';

/**
 * Determines if Sentry should be enabled based on environment
 * - Disabled by default in local development to prevent test data pollution
 * - Can be overridden with REACT_APP_SENTRY_IN_LOCAL=true for local testing
 * - Always enabled in staging/production environments
 */
const sentryEnabled = !isLocal || envVars.sentryInLocal;

/**
 * Initializes Sentry error monitoring in the browser
 *
 * This function sets up comprehensive error tracking with the following capabilities:
 * - Automatic error capture with full stack traces
 * - Performance monitoring for slow operations
 * - Session replay for debugging user interactions
 * - Environment-specific configuration
 *
 * The function is safe to call multiple times - Sentry will ignore subsequent calls.
 *
 * @example
 * // Called once during app initialization
 * initSentryInBrowser();
 *
 * @see https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/
 */
export const initSentryInBrowser = () => {
    if (!sentryEnabled) {
        console.info('[Sentry] Monitoring disabled for local development environment');
        return;
    }

    Sentry.init({
        // Enable/disable Sentry based on environment
        enabled: sentryEnabled,

        sendDefaultPii: true,
        // Project identifier for routing errors to correct Sentry project
        dsn: sentryDsn,

        // Environment tag for filtering errors (local/staging/production)
        environment: axleEnvironment,

        // Include stack traces for all captured messages, not just exceptions
        // This provides better debugging context for non-exception errors
        attachStacktrace: true,

        integrations: [
            // Browser Tracing Integration
            // Tracks page loads, navigation, and user interactions for performance monitoring
            // Automatically instruments fetch/XHR requests and React component renders
            new Sentry.BrowserTracing(),
        ],

        // Error Sampling Configuration
        // 1.0 = capture 100% of errors (recommended for production)
        // Lower values reduce data volume but may miss important errors
        sampleRate: 1.0,

        // Performance Tracing Sample Rate
        // 0.1 = capture 10% of transactions for performance monitoring
        // Balances performance insights with data volume/costs
        tracesSampleRate: 0.1,
    });
};
